import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import {
  cacheAccessibleCompanies,
  getActiveCompany,
  getCachedCompanies,
  setActiveCompany,
} from '../utils/companyContext';
import '../assets/styles/companySelection.css';

import { resolveFileUrl } from '../utils/fileUrl';

const CompanySelection = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(() => getCachedCompanies());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sortedCompanies = useMemo(() => {
    if (!Array.isArray(companies)) {
      return [];
    }
    return [...companies].sort((a, b) => {
      const nameA = (a?.name || a?.company_name || '').toLowerCase();
      const nameB = (b?.name || b?.company_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [companies]);

  useEffect(() => {
    const existingCompany = getActiveCompany();
    if (existingCompany) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanies = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await apiRequest('/api/companies/my-companies', {}, navigate);
        if (!isMounted) {
          return;
        }

        const list = Array.isArray(data?.companies)
          ? data.companies
          : Array.isArray(data)
          ? data
          : [];

        setCompanies(list);
        cacheAccessibleCompanies(list);

        if (list.length === 1) {
          setActiveCompany(list[0]);
          window.location.replace('/dashboard');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load companies.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCompanies();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleSelectCompany = (company) => {
    if (!company) {
      return;
    }
    setActiveCompany(company);
    window.location.replace('/dashboard');
  };

  return (
    <div className="company-selection-page">
      <div className="company-selection-overlay">
        <div className="container py-5">
          <div className="text-center mb-4">
            <p className="badge rounded-pill bg-light text-dark text-uppercase mb-3">Welcome back</p>
            <h1 className="fw-bold text-white display-5">Choose your workspace</h1>
            <p className="text-white-50 lead mb-0">
              Every module will stay in sync with the company you pick here.
            </p>
          </div>

          <div className="row justify-content-center">
            <div className="col-12 col-lg-6">
              {error && (
                <div className="alert alert-danger shadow-sm" role="alert">
                  {error}
                </div>
              )}

              {loading && (
                <div className="d-flex justify-content-center my-5" aria-live="polite">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading companies...</span>
                  </div>
                </div>
              )}

              {!loading && sortedCompanies.length === 0 && (
                <p className="text-center text-white-50 mb-0">
                  No companies found for your account.
                </p>
              )}

              {!loading && sortedCompanies.length > 0 && (
                <div className="company-card-grid">
                  {sortedCompanies.map((company) => {
                    const displayName = company?.name || company?.company_name || 'Company';
                    const code = company?.code || company?.company_code;
                    const logoCandidate =
                      company?.logoUrl ||
                      company?.logo ||
                      company?.company_logo ||
                      company?.companyLogo ||
                      '';
                    const resolvedLogo = resolveFileUrl(logoCandidate);
                    return (
                      <button
                        key={company.id}
                        type="button"
                        className="company-card shadow-lg"
                        onClick={() => handleSelectCompany(company)}
                      >
                        <div className="company-card-body">
                          {resolvedLogo ? (
                            <div className="company-card-logo">
                              <img src={resolvedLogo} alt={`${displayName} logo`} />
                            </div>
                          ) : null}
                          <div className="company-card-pill">{code || 'Company'}</div>
                          <h5 className="company-card-title mb-0">{displayName}</h5>
                          <p className="mb-0 text-white-50">
                            Access all dashboards, approvals, and employees for this company.
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <p className="text-center text-white-50 small mb-0 mt-4">
                You can switch companies later from the dashboard header.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySelection;
